import {
	queryOptions,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type {
	ConsumePasswordResetDto,
	LoginRequest,
	RegisterRequest,
	RequestPasswordResetDto,
	VerifyRequest,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { LinkGen, QueryKeys } from "../LinkGen";

export const useAuth = () => {
	const emit = useAlertEmit();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const register = useMutation({
		mutationFn: async (x: RegisterRequest) => {
			await Api.Register(x);
			emit({
				type: AlertType.SUCCESS,
				content:
					"You have successfully registered. We have sent you an email to verify your account. Please follow the link in your email to activate your account.",
			});
			navigate(LinkGen.Login());
		},
	});

	const login = useMutation({
		mutationFn: async (x: LoginRequest) => {
			await Api.Login(x);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.AuthCheck(),
			});
		},
	});

	const verify = useMutation({
		mutationFn: async (x: VerifyRequest) => {
			await Api.Verify(x);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.AuthCheck(),
			});
		},
		onError: () =>
			emit({
				type: AlertType.ERROR,
				content: "Your activation link is invalid or has expired.",
			}),
	});

	const resendVerification = useMutation({
		mutationFn: async (email: string) => {
			await Api.ResendVerification(email);
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "An email for verification has been sent.",
			});
			navigate(LinkGen.Login());
		},
	});

	const logout = useMutation({
		mutationFn: async () => {
			await Api.Logout();
			queryClient.removeQueries();

			/**
			 * Navigating back to login page is needed because otherwise react-query detects removed queries
			 * too quickly and user will get 401 error on next login (cached from previous session)
			 */
			navigate(LinkGen.Login());
		},
	});

	const requestReset = useMutation({
		mutationFn: async (x: RequestPasswordResetDto) => {
			await Promise.all([
				Api.RequestPasswordReset(x),
				// Wait at least 5s before sending another email
				new Promise((resolve) => setTimeout(resolve, 5_000)),
			]);
		},
		onSuccess: () =>
			emit({
				type: AlertType.SUCCESS,
				content:
					"An email with instructions has been sent. Please follow the email link to finish your password reset.",
			}),
	});

	const resetConsume = useMutation({
		mutationFn: async (x: ConsumePasswordResetDto) => {
			await Api.ConsumePasswordReset(x);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.AuthCheck(),
			});
		},
		onSuccess: () =>
			emit({
				type: AlertType.SUCCESS,
				content: "Your password has been successfully changed.",
			}),
	});

	const acceptInvite = useMutation({
		mutationFn: async (token: string) => {
			await Api.AcceptCompanyInvite(token);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			navigate(LinkGen.Dashboard());
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Your have successfully joined the company.",
			});

			navigate(LinkGen.Dashboard());
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: parseError(error) ?? "Unable to accept invitation",
			});
		},
		onSettled: (_r, _e, x) =>
			queryClient.removeQueries({
				queryKey: QueryKeys.Invitation(x),
			}),
	});

	return {
		login,
		register,
		logout,
		verify,
		requestReset,
		resetConsume,
		resendVerification,
		acceptInvite,
	};
};

const invitationOptions = (token: string | null | undefined) =>
	queryOptions({
		queryKey: QueryKeys.Invitation(token as string),
		queryFn: () =>
			Api.Invitation(token as string) //
				.then((response) => response.data),
		enabled: !!token,
		refetchOnWindowFocus: false,
	});

export const useInvitation = (token: string | null | undefined) => {
	return useQuery(invitationOptions(token));
};
export const useInvitationSuspense = (token: string) => {
	return useSuspenseQuery(invitationOptions(token)).data;
};
